import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BulkUpdateService } from '@/services/agent/bulk/BulkUpdateService';
import { BulkUpdateRequest, BulkUpdateJobStatus } from '@/types/agent/bulk-update';

export const useBulkUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BulkUpdateRequest) => BulkUpdateService.bulkUpdate(request),
    onSuccess: (data) => {
      // Invalidate agents query to refresh agent list after bulk update completes
      queryClient.invalidateQueries({ queryKey: ['all-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start bulk update');
    },
  });
};

export const useBulkUpdateJob = (jobId: number | null, enabled: boolean = true) => {
  return useQuery<BulkUpdateJobStatus | null>({
    queryKey: ['bulk-update-job', jobId],
    queryFn: async () => {
      if (!jobId) return null;

      const job = await BulkUpdateService.getJobStatus(jobId);
      const items = await BulkUpdateService.getJobItems(jobId);

      return {
        job,
        items,
        progress: {
          total: items.length,
          completed: items.filter((i) => i.status === 'completed').length,
          failed: items.filter((i) => i.status === 'failed').length,
          pending: items.filter((i) => i.status === 'pending').length,
        },
      };
    },
    enabled: enabled && jobId !== null,
    refetchInterval: (data) => {
      // Stop polling when job is completed or failed
      if (!data?.job) return 2000;
      if (data.job.status === 'completed' || data.job.status === 'failed') {
        return false;
      }
      return 2000; // Poll every 2 seconds
    },
  });
};
